import { useUpdateCandidate } from "@vidario/api-client";
import {
  computeSkillMatch,
  criteriaToSkillSplit,
  safeHttpUrl,
  showsReplacement,
  userInitials,
  type Candidate,
  type Job,
} from "@vidario/domain";
import {
  Avatar,
  Button,
  Card,
  HiddenBlock,
  cn,
  Markdown,
  StrengthsWeaknesses,
  SubScoreGrid,
  Textarea,
  useToast,
} from "@vidario/ui";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Calendar,
  Check,
  ExternalLink,
  FileText,
  GripVertical,
  Pencil,
  Sparkles,
  X,
} from "lucide-react";
import { useState, type CSSProperties, type ReactElement } from "react";

import { ConfirmOpenButton, attachmentConfirm } from "../../anonym/ConfirmOpenButton";
import { CLASSES_NOTE, HIDDEN_NOTES_TEXT } from "../../anonym/copy";
import { CandidateLocation } from "../../anonym/CandidateLocation";
import { MoveBackToLonglist } from "../candidate-detail/MoveBackToLonglist";
import { useAnonymView, useMaskedCandidate } from "../../anonym/useAnonymView";

export interface RecommendationCardProps {
  /** URL-derived jobId (string), used for cache invalidation. */
  jobId: string;
  candidate: Candidate;
  job: Job;
  rank: number;
  isTop: boolean;
  editable: boolean;
  bookingBaseUrl?: string | undefined;
  onOpen: () => void;
  /** After „Zurück in die Longlist" the card leaves the list (#162). */
  onMovedBack?: () => void;
}

/** One candidate card in the Empfehlungsseite (MVP §4 Screen 5).
 * 2-column layout: hero (ranking + photo + name + title + match) left,
 * must-haves + sub-scores + recommendation text + key findings right.
 * Admins can inline-edit the recommendation text; all users can open the
 * booking link (Terminierung starten) per candidate. */
export function RecommendationCard({
  jobId,
  candidate: c,
  job,
  rank,
  isTop,
  editable,
  bookingBaseUrl,
  onOpen,
  onMovedBack,
}: RecommendationCardProps): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: c.id,
    disabled: !editable,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const { mustHaves: jobMustHaves } = criteriaToSkillSplit(job.criteria);
  const match = computeSkillMatch(c, jobMustHaves, []);
  // cvUrl is scraped external data; sanitise before window.open, which —
  // like href — would happily execute a javascript: URL. Unsafe → hide the
  // "CV ansehen" button entirely.
  const safeCvUrl = safeHttpUrl(c.cvUrl);
  // The Anonym-Ansicht (#140) shows the masked candidate; edits use the real one.
  const anonym = useAnonymView();
  const { candidate: s, hidden } = useMaskedCandidate(c);

  const [editingRec, setEditingRec] = useState(false);
  const [recDraft, setRecDraft] = useState(c.recommendationText ?? "");
  const update = useUpdateCandidate();
  const toast = useToast();

  const saveRec = (): void => {
    update.mutate(
      { jobId, candidateId: c.id, patch: { recommendationText: recDraft || null } },
      {
        onSuccess: () => {
          setEditingRec(false);
          toast.push({
            kind: "success",
            title: "Empfehlung gespeichert",
            message: "Änderungen sind sichtbar, sobald die Stelle freigegeben ist.",
          });
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const openBooking = (): void => {
    if (!bookingBaseUrl) {
      toast.push({
        kind: "warning",
        title: "Terminierung nicht konfiguriert",
        message: "VITE_BOOKING_BASE_URL setzen, um den Booking-Link zu aktivieren.",
      });
      return;
    }
    const url = new URL(bookingBaseUrl);
    url.searchParams.set("kandidat", c.name);
    url.searchParams.set("stelle", job.title);
    url.searchParams.set("ref", c.id);
    window.open(url.toString(), "_blank", "noopener");
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className="overflow-hidden p-0"
      padded={false}
      data-testid={`recommendation-card-${c.id}`}
    >
      <div className="grid grid-cols-1 gap-0 lg:grid-cols-[260px_minmax(0,1fr)]">
        <div
          className={cn(
            "flex flex-col items-start gap-4 border-b border-border-default p-5 sm:p-7 lg:border-r lg:border-b-0",
            isTop
              ? "bg-[linear-gradient(160deg,var(--color-primary-50)_0%,#fff_70%)]"
              : "bg-neutral-50",
          )}
        >
          <div className="flex w-full items-start gap-3">
            {editable && (
              <button
                type="button"
                aria-label="Ranking verschieben"
                className="mt-3.5 cursor-grab text-neutral-400 active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                {...attributes}
                {...listeners}
              >
                <GripVertical size={18} />
              </button>
            )}
            <div className="flex items-center gap-4">
              <span
                className={cn(
                  "tabular-nums -tracking-[2px] leading-[0.9]",
                  isTop ? "text-primary-500" : "text-neutral-900",
                )}
                style={{ fontSize: "var(--text-display)", fontWeight: 700 }}
              >
                {String(rank).padStart(2, "0")}
              </span>
              {isTop && (
                <span className="rounded-sm bg-primary-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-0">
                  Top Pick
                </span>
              )}
            </div>
          </div>
          <Avatar
            initials={anonym ? "" : userInitials(s.name)}
            variant={anonym ? "placeholder" : "initials"}
            size={64}
          />
          <div>
            <div
              id={`recommendation-name-${c.id}`}
              tabIndex={-1}
              className={cn(
                "text-[17px] font-semibold -tracking-[0.2px] focus:outline-none",
                anonym && "font-mono",
              )}
            >
              {s.name}
            </div>
            {s.currentTitle && (
              <div className="mt-0.5 text-[13px] text-text-secondary">{s.currentTitle}</div>
            )}
            {s.currentCompany && (
              <div className="text-[13px] text-text-tertiary">{s.currentCompany}</div>
            )}
            <CandidateLocation
              className="mt-2"
              location={s.location}
              distanceKm={s.distanceKm}
              jobLocation={job.location}
              anonym={anonym}
            />
          </div>
          {c.score != null && (
            <div className="w-full border-t border-border-default pt-4">
              <div className="mb-1.5 flex justify-between text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                <span>Match</span>
                <span className="tabular-nums text-sm text-success-600">{c.score}%</span>
              </div>
              <div className="h-1 overflow-hidden rounded-[2px] bg-neutral-100">
                <div
                  aria-hidden="true"
                  className="h-full"
                  style={{
                    width: `${c.score}%`,
                    background: "linear-gradient(90deg, var(--color-success-500), #22C55E)",
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="min-w-0 p-5 sm:p-7">
          <div className="grid grid-cols-1 items-start gap-8 sm:grid-cols-2">
            <div>
              <h4 className="m-0 mb-2.5 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                Must-Haves der Stelle
              </h4>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {jobMustHaves.map((s) => {
                  const met = match.mustHit.includes(s);
                  return (
                    <div key={s} className="flex items-center gap-2 text-[13px]">
                      {met ? (
                        <Check size={14} strokeWidth={2.5} className="shrink-0 text-success-600" />
                      ) : (
                        <X size={14} strokeWidth={2.5} className="shrink-0 text-neutral-400" />
                      )}
                      <span
                        className={cn(
                          met ? "text-text-primary" : "text-text-tertiary line-through",
                        )}
                      >
                        {s}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            {Object.keys(c.subScores).length > 0 && (
              <div>
                <h4 className="m-0 mb-2.5 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                  Detailbewertung
                </h4>
                <SubScoreGrid sub={c.subScores} />
              </div>
            )}
          </div>

          {(c.strengths.length > 0 || c.weaknesses.length > 0) && (
            <div className="mt-5 border-t border-border-default pt-5">
              <h4 className="m-0 mb-3 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                Stärken &amp; Schwächen
              </h4>
              <StrengthsWeaknesses
                strengths={s.strengths}
                weaknesses={s.weaknesses}
                strengthsHidden={Boolean(hidden?.strengths && c.strengths.length > 0)}
                weaknessesHidden={Boolean(hidden?.weaknesses && c.weaknesses.length > 0)}
              />
              {showsReplacement(
                [...c.strengths, ...c.weaknesses],
                [...s.strengths, ...s.weaknesses],
              ) && <p className="m-0 mt-3 text-xs text-text-tertiary">{CLASSES_NOTE}</p>}
            </div>
          )}

          <div className="mt-5 border-t border-border-default pt-5">
            <div className="mb-2.5 flex items-center gap-2">
              <Sparkles size={13} className="text-primary-600" />
              <h4 className="m-0 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                Vidario-Empfehlung
              </h4>
              {editable && !editingRec && !anonym && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  onClick={() => setEditingRec(true)}
                >
                  <Pencil size={11} /> bearbeiten
                </button>
              )}
            </div>
            {anonym ? (
              <HiddenBlock>{HIDDEN_NOTES_TEXT}</HiddenBlock>
            ) : editingRec ? (
              <>
                <Textarea
                  aria-label="Empfehlungstext"
                  rows={4}
                  value={recDraft}
                  onChange={(e) => setRecDraft(e.target.value)}
                />
                <div className="mt-2 flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setEditingRec(false);
                      setRecDraft(c.recommendationText ?? "");
                    }}
                  >
                    Abbrechen
                  </Button>
                  <Button size="sm" loading={update.isPending} onClick={saveRec}>
                    Speichern
                  </Button>
                </div>
              </>
            ) : (
              <Markdown empty="Noch kein Empfehlungstext hinterlegt.">
                {c.recommendationText ?? ""}
              </Markdown>
            )}
          </div>

          {!anonym && c.interviewKeyFindings.length > 0 && (
            <div className="mt-5 border-t border-border-default pt-5">
              <h4 className="m-0 mb-2.5 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                Key-Findings aus dem Tiefeninterview
              </h4>
              <ul className="m-0 flex flex-col gap-1.5 list-none p-0">
                {c.interviewKeyFindings.map((f) => (
                  <li key={f} className="flex gap-2.5 text-[13px] leading-relaxed">
                    <span
                      aria-hidden="true"
                      className="mt-2 inline-block h-1 w-1 shrink-0 rounded-full bg-primary-500"
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-border-default pt-4">
            {editable && (
              <MoveBackToLonglist
                jobId={jobId}
                candidate={c}
                job={job}
                isAdmin
                className="mr-auto"
                {...(onMovedBack && { onMoved: onMovedBack })}
              />
            )}
            {safeCvUrl && anonym && (
              <ConfirmOpenButton
                url={safeCvUrl}
                candidateId={c.id}
                action="attachment_opened"
                kind="cv"
                {...attachmentConfirm("Lebenslauf")}
                className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-3 text-sm font-medium text-primary-600 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <FileText size={14} aria-hidden="true" /> CV ansehen
              </ConfirmOpenButton>
            )}
            {safeCvUrl && !anonym && (
              <Button
                variant="tertiary"
                size="sm"
                leftIcon={<FileText size={14} />}
                onClick={() => window.open(safeCvUrl, "_blank", "noopener")}
              >
                CV ansehen
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              rightIcon={<ExternalLink size={14} />}
              onClick={onOpen}
            >
              Vollständiges Profil
            </Button>
            {anonym ? (
              // The booking link carries the name.
              <span className="self-center text-xs text-text-tertiary">
                Terminierung in der Anonym-Ansicht nicht verfügbar
              </span>
            ) : (
              <Button size="sm" leftIcon={<Calendar size={14} />} onClick={openBooking}>
                Terminierung starten
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
